const { jQuery: $ } = window;

import debounce from "./debounce";

/**
 * Controls a single `[kmdg-post-filters]` shortcode on the page. Each instance owns its own DOM element,
 * StateStore, in-flight request, lock flag, and transition timers, so multiple shortcodes on one page never
 * share state or interfere. It binds its own filter/search/pagination/clear handlers and hosts the history
 * state-restore logic, delegating data fetching, rendering, pagination and history to the shared services.
 */
export default class ShortcodeInstance {
    /** @member {jQuery} */
    #$el;

    /** @member {StateStore} */
    #store;

    /** @member {object} Shared services: {hooks, logger, rest, renderer, pagination, history, select2Opts}. */
    #services;

    /** @member {jqXHR} This instance's in-flight /posts request (for abort). */
    currentRequest = null;

    /** @member {jqXHR} This instance's in-flight /filters request. Separate slot so filter-data can run in
     *  parallel with /posts on a filter change without either aborting the other. */
    currentFilterRequest = null;

    /** @member {boolean} Suppresses change handlers during programmatic updates. */
    locked = false;

    /** @member {int[]} Pending post-transition timeout ids. */
    transitions = [];

    /** @member {FilterControl[]} Filter-control adapters for this shortcode's filters. */
    #adapters = [];

    /** @member {jQuery} Cached search input (set in #bindControls; reused across handlers/restore paths). */
    #$search = null;

    /** @member {jQuery} Cached sort select (set in #bindControls). */
    #$sort = null;

    /**
     * @param {jQuery} $el
     * @param {StateStore} store
     * @param {object} services
     */
    constructor($el, store, services) {
        this.#$el = $el;
        this.#store = store;
        this.#services = services;
    }

    /** @returns {jQuery} */
    get $el() {
        return this.#$el;
    }

    /** @returns {StateStore} */
    get store() {
        return this.#store;
    }

    /** @returns {string} */
    get id() {
        return this.#$el.attr("id");
    }

    get #hooks() {
        return this.#services.hooks;
    }

    get #logger() {
        return this.#services.logger;
    }

    /** @returns {object} Shared services (exposed so filter adapters can reach hooks/rest/select2Opts). */
    get services() {
        return this.#services;
    }

    /** @returns {FilterControl[]} This shortcode's filter-control adapters (read-only; used by AppliedFilters). */
    get adapters() {
        return this.#adapters;
    }

    /**
     * Order-insensitive equality for filter values, treating scalars and arrays uniformly (both normalized
     * to arrays of strings). Used by the default reconcile so a matching multi-value default (an array)
     * isn't mistaken for a change and doesn't force an unnecessary refetch on load.
     *
     * @param {*} a
     * @param {*} b
     * @returns {boolean}
     */
    #sameValue(a, b) {
        const toArray = (v) => Array.isArray(v)
            ? v.map(String)
            : (v === null || v === undefined ? [] : [String(v)]);

        const arrA = toArray(a);
        const arrB = toArray(b);

        if(arrA.length !== arrB.length) {
            return false;
        }

        const setB = new Set(arrB);
        return arrA.every((x) => setB.has(x));
    }

    /* ---- Delegating convenience methods (used by handlers and services) ---- */

    /**
     * @param {int} count
     * @param {Function} callback
     * @param {object} [opts] Forwarded to RestClient.getPosts (e.g. { background: true } to skip the spinner).
     */
    getPosts(count, callback, opts = {}) {
        this.#services.rest.getPosts(this, count, callback, opts);
    }

    /**
     * Fetches batch filter-data (narrowed taxonomy options and/or disjunctive counts) for the current query
     * state; the response is handed to {@see applyFilterData()}. Fired once on init and, when counts are on,
     * in parallel with getPosts on every filter/search/sort change.
     *
     * @param {object} opts { withOptions, withCounts }
     * @param {Function} [callback]
     */
    getFilterData(opts, callback) {
        this.#services.rest.getFilterData(this, opts, callback);
    }

    /**
     * Distributes batch filter-data to each filter control. `data` is keyed by filter name →
     * `{ options?, counts? }`; each adapter re-seeds its options and/or renders the "(N)" suffix + disables
     * zero-count options as applicable to its UI. Called from RestClient after every /filters fetch.
     *
     * @param {object} data
     */
    applyFilterData(data) {
        this.#adapters.forEach((adapter) => {
            if(typeof adapter.applyFilterData === "function") {
                adapter.applyFilterData(data[adapter.name] || {});
            }
        });
    }

    /**
     * @param {array} posts
     * @param {boolean} clear
     * @param {int} delay
     * @param {Function} done
     * @param {boolean} animate False renders instantly (no entry animation) — used on history restore.
     */
    transition(posts, clear = false, delay = 150, done = null, animate = true) {
        this.#services.renderer.transitionPosts(this, posts, clear, delay, done, animate);
    }

    loadPagination() {
        this.#services.pagination.load(this);
    }

    /** Re-renders the applied-filter chips + results count. Called on init and after every posts fetch. */
    loadAppliedFilters() {
        this.#services.appliedFilters.load(this);
    }

    /**
     * This instance's active selection expressed for the URL hash: `{ params: { urlSlug: slug[] }, search }`.
     * Keyed by each filter's (overridable) URL slug, values are option slugs. Consumed by HistoryManager to
     * build the page-global fragment.
     *
     * @returns {{params: Object<string,string[]>, search: string}}
     */
    hashParams() {
        const params = {};

        this.#adapters.forEach((adapter) => {
            if(typeof adapter.getSelectionSlugs !== "function") {
                return;
            }
            const slugs = adapter.getSelectionSlugs();
            if(slugs.length) {
                params[adapter.urlSlug] = slugs;
            }
        });

        return { params, search: this.#store.get("searchQuery") || "" };
    }

    /**
     * Runs a multi-adapter mutation (Clear, hash restore, initial reconcile) as a SINGLE reload cycle instead
     * of one full reload per adapter. `mutate` makes the adapter/DOM/store changes under `this.locked`, so a
     * per-adapter `fireChange()` still syncs its widget (e.g. select2) but its change handler early-returns
     * rather than reloading. Afterwards this rebuilds `filterSelected` from the adapters, resets to page 1,
     * and fires one getPosts (+ optionally one counts fetch), optionally writing the URL hash.
     *
     * @param {Function} mutate Applies the adapter/search/store changes.
     * @param {object}   opts
     * @param {boolean|null} opts.push `true`/`false` → push/replace the URL hash; `null` (default) → don't
     *                                 touch the URL (e.g. hash restore, where the fragment is already set).
     * @param {boolean} opts.fetchCounts Fire the parallel counts refresh (default true). Init passes false on
     *                                   the restore/adopt paths, where init's trailing getFilterData already
     *                                   fetches counts for the same state — so this would just be aborted.
     */
    #batchReload(mutate, { push = null, fetchCounts = true } = {}) {
        const store = this.#store;

        this.locked = true;
        try {
            mutate();
        } finally {
            this.locked = false;
        }

        // Single source of truth after the mutation: rebuild filterSelected from the adapters' live values.
        const selected = store.get("filterSelected") || {};
        this.#adapters.forEach((adapter) => { selected[adapter.name] = adapter.getValue(); });
        store.set("filterSelected", selected);
        store.set("filterPage", 1);
        store.cache = [];

        this.getPosts(store.get("filterLimit"), (posts) => {
            this.transition(posts, true);
        });
        if(fetchCounts && store.get("useCounts")) {
            this.getFilterData({ withCounts: true });
        }
        if(push !== null) {
            this.syncUrl(push);
        }
    }

    /**
     * Applies a hash fragment's state to this instance (on load): sets each adapter from its matching
     * url-slug params (unmatched adapters reset), applies the search, then refetches once via {@see
     * #batchReload()}. Does NOT write the URL — the fragment is already there.
     *
     * @param {Object<string,string[]>} paramMap Keyed by url slug.
     * @param {string} search
     * @param {object} [opts]
     * @param {boolean} [opts.fetchCounts=true] Passed through to #batchReload — init sets false because its
     *                                          trailing getFilterData already refreshes counts for this state.
     */
    applyHashParams(paramMap, search, { fetchCounts = true } = {}) {
        this.#batchReload(() => {
            this.#adapters.forEach((adapter) => {
                const slugs = (typeof adapter.urlSlug !== "undefined") ? paramMap[adapter.urlSlug] : null;

                if(slugs && typeof adapter.setValueBySlugs === "function") {
                    adapter.setValueBySlugs(slugs);
                } else {
                    adapter.reset();
                }
                adapter.fireChange(); // sync the widget; the lock suppresses the reload handler
            });

            this.#$search.val(search || "");
            this.#store.set("searchQuery", search || "");
        }, { push: null, fetchCounts });
    }

    /**
     * Reflects this instance's state into the URL hash (history instances only — pre-filtered/non-history
     * feeds never touch the URL). Delegates to the page-level HistoryManager, which rebuilds the shared
     * fragment from all history instances and writes it (debounced).
     *
     * @param {boolean} push Push a new history entry (discrete change/clear) or replace in place (search).
     */
    syncUrl(push = true) {
        if(!this.#store.get("history")) {
            return;
        }
        this.#services.history.syncFragment(push);
    }

    /**
     * @param {string|null} url
     */
    saveState(url = null) {
        this.#services.history.save(url);
    }

    /**
     * How this page load was reached — `"navigate"` | `"reload"` | `"back_forward"` (`"prerender"` maps to
     * navigate). Drives the load-time precedence between the saved history entry and the URL fragment: only a
     * back/forward restores the cached entry; a refresh or fresh URL applies the fragment. Uses the Navigation
     * Timing API, falling back to the legacy `performance.navigation` and finally to "navigate".
     *
     * @returns {string}
     */
    #navigationType() {
        try {
            const nav = performance.getEntriesByType("navigation")[0];
            if(nav && nav.type) {
                return nav.type === "prerender" ? "navigate" : nav.type;
            }
        } catch(e) { /* Navigation Timing L2 unavailable */ }

        const legacy = (typeof performance !== "undefined" && performance.navigation)
            ? performance.navigation.type
            : 0;
        return legacy === 2 ? "back_forward" : (legacy === 1 ? "reload" : "navigate");
    }

    /**
     * Boots the instance: resolves settings, restores from browser history if present (or primes the
     * cache when history is enabled), then binds events and renders pagination.
     *
     * @param {object|null} savedState The serialized state for this shortcode from browser history, if any.
     */
    init(savedState) {
        const $el = this.#$el;
        const store = this.#store;

        // default_settings only seeds `defaults` + the initial requiresReload flag here; runtime reads use
        // the element's data-settings. (Left as-is intentionally; flagged for a future cleanup.)
        const filtered = this.#hooks.applyFilter("default_settings", $el.data("settings"), $el.get(0));
        const defaults = structuredClone(filtered);

        // Seed defaults + initial flags BEFORE binding filters: a reconcile-triggered refresh in
        // #bindFilters (and the history prepopulate below) runs getPosts, which reads `defaults`.
        store.set("defaults", defaults);
        if(!savedState) {
            store.cache = [];
            store.requiresReload = !!filtered.pagination;
        }

        this.#$el.on("submit", (e) => {
            e.preventDefault();
            return false;
        });

        // Create + initialise + bind the filter adapters. Adapters must exist before loadState() (it
        // restores filter values through them), which is why the branch below runs after this. #bindFilters
        // COLLECTS (doesn't apply) any pre-selected defaults that differ from the render, so they can be
        // adopted in a single reload rather than one per filter.
        const toAdopt = this.#bindFilters();
        this.#bindControls();

        // Exactly one of these paths fetches on load (or none). What's authoritative depends on how the page
        // was reached (see #navigationType): BACK/FORWARD restores the saved history entry (its cached posts,
        // no refetch); a REFRESH or a fresh/shared/typed URL applies the URL fragment — so a bookmarked or
        // hand-edited `#/type/x` always reflects the URL, even when a stale saved state is present. (Same-
        // document back/forward doesn't re-run init at all — HistoryManager.listen handles it.) After that:
        // adopt pre-selected defaults, else prime the history cache.
        const frag = store.get("history") ? this.#services.history.fragmentFor(this) : { params: {}, search: "" };
        const hasFrag = store.get("history") && (Object.keys(frag.params).length || frag.search);
        const backForward = this.#navigationType() === "back_forward";

        if((backForward || !!filtered.pagination) && savedState) {
            // Restore the saved entry (cached posts + controls, rendered instantly by loadState) when:
            //  • BACK/FORWARD to any history feed, or
            //  • a REFRESH of a PAGINATION feed — the numbered page lives in the URL path (/page/N/), but the
            //    server can't render page N for a static-page URL, so the cached page must be restored.
            // A refresh of a LOAD-MORE feed deliberately does NOT restore: there's no URL representation of the
            // accumulated pages to honour, so it falls through to the fragment / prime paths below and resets
            // to page 1 (reloading starts the feed over).
            this.#logger.log("Loading historical posts...");
            this.loadState(savedState, true);
            store.set("defaults", defaults); // loadState's restore replaced settings; re-affirm defaults.
        } else if(hasFrag) {
            // A refresh, a fresh/shared/typed URL with a filter fragment, or a back/forward whose entry
            // carried no saved state → apply the URL state at page 1 (a refresh thus resets any accumulated
            // load-more cache). fetchCounts:false — the trailing getFilterData below refreshes counts.
            this.applyHashParams(frag.params, frag.search, { fetchCounts: false });
        } else if(toAdopt.length) {
            // Adopt pre-selected defaults that differ from the render — all in ONE reload. Replace (not push):
            // this is the initial state, not a user navigation, so it shouldn't add a back entry.
            this.#batchReload(() => {
                toAdopt.forEach(({ adapter, value }) => {
                    adapter.setValue(value);
                    adapter.fireChange(); // sync the widget; the lock suppresses the reload handler
                });
            }, { push: false, fetchCounts: false });
        } else if(store.get("history")) {
            // Prime the client cache in the BACKGROUND so a same-document back/forward to this (initial) entry
            // can re-render page 1 from cache — the server render is gone by then. No loading indicator: the
            // server-rendered posts are already on screen and unchanged, so a dim/spinner would be misleading.
            this.getPosts(store.get("filterLimit"), () => {
                this.saveState();
            }, { background: true });
        }

        // One batch filter-data request (JS-only, async — no added page-render cost): narrows taxonomy option
        // lists when any taxonomy filter is present, and primes disjunctive counts when enabled. This single
        // call replaces both the old per-taxonomy /filters narrowing and the /posts count prime. Reads the
        // current (possibly history-restored) query state, so it reflects any restored selections.
        const hasTaxonomy = this.#$el.find(".kpfsc__filter--taxonomy").length > 0;
        const useCounts = !!store.get("useCounts");
        if(hasTaxonomy || useCounts) {
            this.getFilterData({ withOptions: hasTaxonomy, withCounts: useCounts });
        }

        this.loadPagination();
        this.loadAppliedFilters(); // initial chips (from server-rendered selections) + count
    }

    /**
     * Creates a filter-control adapter for every `.kpfsc__filter`, dispatched by its `data-filter-type`
     * (absent = "select"), initialises it, reconciles its value against the stored default, and binds the
     * generic change → reload handler. Only reading/observing the control is delegated to the adapter; the
     * reload logic (update filterSelected, refetch, render) stays here so it's type-agnostic.
     *
     * Reconciliation is COLLECTED, not applied here: any adapter whose stored default differs from its
     * rendered value is returned in the adopt-list so init() can apply them all in one {@see #batchReload()}
     * (rather than firing one reload per adapter during binding).
     *
     * @returns {{adapter: FilterControl, value: *}[]} Adapters needing their default adopted.
     */
    #bindFilters() {
        const store = this.#store;
        this.#adapters = [];
        const toAdopt = [];

        this.#$el.find(".kpfsc__filter").each((i, el) => {
            const $filter = $(el);
            const type = $filter.attr("data-filter-type") || "select";
            const adapter = this.#services.filters.create(type, $filter, this);

            if(!adapter) {
                return;
            }

            this.#adapters.push(adapter);
            adapter.init();

            const name = adapter.name;
            const defaults = store.get("filterSelected");

            // If the stored default differs from the rendered value, flag it for adoption (applied later, in
            // one batch). Uses an array-aware comparison so a matching multi-value default isn't seen as
            // "changed". NOT applied/fired here — init() adopts the whole list in a single reload.
            if(defaults[name] && !this.#sameValue(defaults[name], adapter.getValue())) {
                toAdopt.push({ adapter, value: defaults[name] });
            }

            // When a filter changes, reset to page 1 and reload posts.
            adapter.bindChange(() => {
                if(this.locked) return;

                store.set("filterPage", 1);

                const selected = store.get("filterSelected");
                selected[name] = adapter.getValue();
                store.set("filterSelected", selected);

                store.cache = [];

                this.getPosts(store.get("filterLimit"), (posts) => {
                    this.transition(posts, true);
                });

                // Recompute disjunctive counts in parallel (options are static, so counts only).
                if(store.get("useCounts")) {
                    this.getFilterData({ withCounts: true });
                }

                // Reflect the new selection into the URL hash (history instances only; pushes a new entry).
                this.syncUrl(true);
            });
        });

        return toAdopt;
    }

    /**
     * Binds the clear button, search bar, load-more button, and numbered pagination.
     */
    #bindControls() {
        const store = this.#store;

        // Cache the search input + sort select once — reused here and by the clear / restore paths.
        this.#$search = this.#$el.find(".kpfsc__search-bar");
        this.#$sort = this.#$el.find(".kpfsc__sort-select");

        // Clear / reset — one reload cycle for the whole form (was one per visible filter). Hidden filters
        // are left untouched, so #batchReload's filterSelected re-sync keeps their rendered defaults.
        this.#$el.find(".kpfsc__clear").on("click", () => {
            const defaults = store.get("defaults");

            this.#logger.log("Clearing form...");
            store.requiresReload = false;

            this.#batchReload(() => {
                this.#$el.get(0).reset();
                this.#$search.val("");
                store.replace(structuredClone(defaults));
                store.set("defaults", defaults); // replace() wiped defaults; re-affirm
                store.set("searchQuery", "");

                this.#adapters.forEach((adapter) => {
                    if(adapter.$el.hasClass("kpfsc--hidden")) {
                        return;
                    }
                    adapter.reset();
                    adapter.fireChange(); // sync widget; the lock suppresses the reload handler
                });
            }, { push: true });
        });

        // Search
        const $search = this.#$search;
        $search.on("keyup change", debounce(250, false, () => {
            if(this.locked) return;

            this.#logger.log("Searching posts...");
            store.set("searchQuery", $search.val());
            store.set("filterPage", 1); // Reset to first page (was previously a side-effect of transitionPosts)

            store.cache = [];

            this.getPosts(store.get("filterLimit"), (posts) => {
                this.transition(posts, true);
            });

            if(store.get("useCounts")) {
                this.getFilterData({ withCounts: true });
            }

            // Reflect the search into the URL hash in place (replace, not push — typing shouldn't spam history).
            this.syncUrl(false);
        }));

        // Sort direction — flips the direction of the configured orderby field(s). The orderby fields are
        // author-set (via the `order` attr) and round-trip through queryMeta.query.order; this control only
        // rewrites their direction, then refetches (server re-validates via sanitizeOrders).
        const $sort = this.#$sort;
        $sort.on("change", () => {
            if(this.locked) return;

            const dir = $sort.val() === "ASC" ? "ASC" : "DESC";
            this.#logger.log("Sorting posts:", dir);

            const meta = store.get("queryMeta");
            if(!meta || !meta.query) return; // server always seeds queryMeta; guard the write defensively

            const order = meta.query.order || {};
            for(const field in order) {
                order[field] = dir;
            }
            meta.query.order = order;
            store.set("queryMeta", meta);
            store.set("filterPage", 1);

            store.cache = [];

            // No filter-data refresh: sort direction never changes which posts match, so every facet count
            // is identical — a counts fetch here would just repeat the previous result.
            this.getPosts(store.get("filterLimit"), (posts) => {
                this.transition(posts, true);
            });
        });

        // Load more
        this.#$el.find(".kpfsc__load-more").on("click", (e) => {
            e.preventDefault();
            const page = store.get("filterPage") + 1;

            this.#logger.log("Loading More Posts...");
            store.set("filterPage", page);

            this.getPosts(store.get("filterMore"), (posts) => {
                this.transition(posts);
            });

            return false;
        });

        // Numbered pagination
        this.#$el.find(".kpfsc__paginationNumbers").on("click", ".kpfsc__pageBtn", (e) => {
            e.preventDefault();
            const $btn = $(e.currentTarget);

            this.#logger.log("Changing to page:", $btn.data("page"));

            store.set("filterPage", $btn.data("page"));

            // Create a history checkpoint (refined later by getPosts via replaceState).
            if(store.get("history")) {
                this.saveState($btn.attr("href"));
            }

            store.cache = [];

            this.getPosts(store.get("filterMore"), (posts) => {
                this.loadPagination();
                this.transition(posts, true);

                // Move focus to the results region so keyboard/screen-reader users land on the new page's
                // content (preventScroll — the animate below owns the viewport movement).
                const postsEl = this.#$el.find(".kpfsc__posts").attr("tabindex", "-1").get(0);
                if(postsEl) postsEl.focus({ preventScroll: true });

                // Scroll to top of posts
                $('html, body').animate({
                    scrollTop: this.#$el.offset().top + this.#hooks.applyFilter("scroll_offset", 0)
                }, 500, 'swing', () => {
                    this.#hooks.doAction("finished_scrolling");
                });
            });
        });
    }

    /**
     * Restores this shortcode from a saved history state: writes the settings, re-applies filter/search
     * values under lock (so change handlers don't fire), and — when reload is true — re-renders from the
     * cached posts (no network request).
     *
     * @param {{settings: object, cache: object[], requiresReload: boolean}} state
     * @param {boolean} reload
     * @returns {object} The restored settings.
     */
    loadState(state, reload = true) {
        const store = this.#store;
        const $el = this.#$el;
        const settings = state.settings;
        const historyEnabled = store.get("history");

        // Only load posts if history is enabled for this shortcode.
        if(historyEnabled) {
            store.restore(state);

            this.locked = true;

            // Re-apply this instance's filter values through their adapters (fireChange syncs widgets like
            // select2; the lock prevents the change handler from triggering a reload).
            this.#adapters.forEach((adapter) => {
                const value = settings.filterSelected[adapter.name];
                if(value) {
                    adapter.setValue(value);
                } else {
                    adapter.reset();
                }
                adapter.fireChange();
            });

            // Re-apply search
            this.#$search.val(settings.searchQuery);

            // Re-apply sort direction from the restored order map (first field's direction).
            const restoredOrder = settings.queryMeta?.query?.order || {};
            const restoredDir = Object.values(restoredOrder)[0];
            if(restoredDir) {
                this.#$sort.val(String(restoredDir).toUpperCase() === "ASC" ? "ASC" : "DESC");
            }

            this.locked = false;

            if(reload) {
                // Always clear + replace from the cache (not append): the cache is the authoritative post list
                // for the restored entry, so appending would duplicate the server-rendered page. animate:false
                // — a restore should appear settled instantly, not re-run the entry animation after the page
                // has already loaded.
                this.transition(store.cache, true, 0, () => {
                    // Settings restore already set the correct filterPage (no side-effect reset any more).
                    // Mirror the server $moreClass (and RestClient): also stay hidden when load-more is off.
                    if(!settings.showMore || settings.queryMeta.pages <= settings.filterPage) {
                        $el.addClass("kpfsc--no-more");
                    } else {
                        $el.removeClass("kpfsc--no-more");
                    }

                    if(store.cache.length === 0) {
                        $el.addClass("kpfsc--no-posts");
                    } else {
                        $el.removeClass("kpfsc--no-posts");
                    }

                    this.loadPagination();
                    this.loadAppliedFilters();
                }, false);
            }
        }

        return settings;
    }
}
